import { Action, Reducer } from 'redux';
import { Cart, CartActionTypes, CartState, CartTypes } from './cartTypes';
import {
  calculateTotalParts,
  calculateTotalPrice,
  calculateTotalShippingCost
} from './cartUtils';

export const INITIAL_STATE: CartState = {
  cartItems: {
    data: [],
    errorMessages: null,
    isError: false,
    isLoading: false,
    isLoadingUpdate: false,
    idPartIsLoading: null,
    dataNoSupplierAssigned: null,
    dataUnansweredItems: null,
    dataUnansweredCanceledItems: null,
    dataUnansweredRequotedItems: null
  },
  summary: {
    total: 0,
    shippingCost: 0,
    totalParts: 0
  },
  errorMessagesGeneral: []
};

const cartReducer: Reducer<CartState> = (
  state: CartState = INITIAL_STATE,
  action: Action
) => {
  const actionTypes = action as CartActionTypes;
  switch (actionTypes.type) {
    case CartTypes.ADD_CART:
      const findSupplierAdd = state.cartItems.data.find(
        item => item.guid === actionTypes.payload.data.guid
      );
      const updatedPartsStateAdd = [];

      if (findSupplierAdd) {
        const updatedPartsAdd = [
          ...findSupplierAdd.parts,
          actionTypes.payload.data.parts[0]
        ];
        const calculateTotalPartsAdd = updatedPartsAdd.reduce(
          (acc, item) => acc + item.quantity,
          0
        );
        const calculateTotalPartsPriceAdd = updatedPartsAdd.reduce(
          (acc, item) => acc + item.price * item.quantity,
          0
        );
        const calculateTotalShippingCostAdd = updatedPartsAdd.reduce(
          (acc, item) => acc + item.shippingCost,
          0
        );
        updatedPartsStateAdd.push({
          ...findSupplierAdd,
          parts: updatedPartsAdd,
          totalPartsPrice: calculateTotalPartsPriceAdd,
          totalShippingCost: calculateTotalShippingCostAdd,
          totalParts: calculateTotalPartsAdd
        });
      }
      const updatedCartItems = findSupplierAdd
        ? [
            ...state.cartItems.data.filter(
              item => item.guid !== actionTypes.payload.data.guid
            ),
            ...updatedPartsStateAdd
          ]
        : [...state.cartItems.data, actionTypes.payload.data];

      const updatedTotal = calculateTotalPrice(updatedCartItems);
      const updatedShippingCost = calculateTotalShippingCost(updatedCartItems);
      const updatedTotalParts = calculateTotalParts(updatedCartItems);
      return {
        ...state,
        cartItems: {
          ...state.cartItems,
          isLoading: false,
          isLoadingUpdate: false,
          errorMessages: null,
          idPartIsLoading: null,
          isError: false,
          data: updatedCartItems
        },
        summary: {
          total: updatedTotal,
          shippingCost: updatedShippingCost,
          totalParts: updatedTotalParts
        }
      };

    case CartTypes.UPDATE_CART:
      const findSupplier = state.cartItems.data.find(
        item => item.guid === actionTypes.payload.guidSupplier
      );
      if (!findSupplier) return state;
      const updatedPartsFilter = findSupplier.parts.filter(
        item =>
          !(
            item.guidQuoteSupplierItem ===
              actionTypes.payload.data.guidQuoteSupplierItem &&
            item.idPartType === actionTypes.payload.data.idPartType
          )
      );
      const updatedParts = [...updatedPartsFilter, actionTypes.payload.data];
      const calculateTotalPartsUpdate = updatedParts.reduce(
        (acc, item) => acc + item.quantity,
        0
      );
      const calculateTotalPartsPriceUpdate = updatedParts.reduce(
        (acc, item) => acc + item.price * item.quantity,
        0
      );
      const calculateTotalShippingCostUpdate = updatedParts.reduce(
        (acc, item) => acc + item.shippingCost,
        0
      );
      const updatedSupplier = {
        ...findSupplier,
        parts: updatedParts.sort((a, b) =>
          a.partName.localeCompare(b.partName)
        ),
        totalPartsPrice: calculateTotalPartsPriceUpdate,
        totalShippingCost: calculateTotalShippingCostUpdate,
        totalParts: calculateTotalPartsUpdate
      };

      const updatedCartItemsUpdate = [
        ...state.cartItems.data.filter(
          item => item.guid !== actionTypes.payload.guidSupplier
        ),
        updatedSupplier
      ];
      const updatedTotalUpdate = calculateTotalPrice(updatedCartItemsUpdate);

      const updatedTotalPartsUpdate = calculateTotalParts(
        updatedCartItemsUpdate
      );
      return {
        ...state,
        cartItems: {
          ...state.cartItems,
          isLoading: false,
          isLoadingUpdate: false,
          errorMessages: null,
          isError: false,
          idPartIsLoading: null,
          data: updatedCartItemsUpdate
        },
        summary: {
          total: updatedTotalUpdate,
          shippingCost: state.summary.shippingCost,
          totalParts: updatedTotalPartsUpdate
        }
      };
    case CartTypes.SAVE_CART:
      const savedData = actionTypes.payload.data;
      const updatedTotalSaved = calculateTotalPrice(savedData);
      const updatedShippingCostSaved = calculateTotalShippingCost(savedData);
      const updatedTotalPartsSaved = calculateTotalParts(savedData);

      return {
        ...state,
        cartItems: {
          ...state.cartItems,
          isLoading: false,
          isLoadingUpdate: false,
          idPartIsLoading: null,
          errorMessages: actionTypes.payload.errorMessages,
          isError: actionTypes.payload.isError,
          data: actionTypes.payload.data,
          dataNoSupplierAssigned: actionTypes.payload.dataNoSupplierAssigned,
          dataUnansweredItems: actionTypes.payload.dataUnansweredItems,
          dataUnansweredCanceledItems:
            actionTypes.payload.dataUnansweredCanceledItems,
          dataUnansweredRequotedItems:
            actionTypes.payload.dataUnansweredRequotedItems
        },
        summary: {
          total: updatedTotalSaved,
          shippingCost: updatedShippingCostSaved,
          totalParts: updatedTotalPartsSaved
        }
      };
    case CartTypes.REMOVE_CART:
      const updatedData = state.cartItems.data
        .map(item => ({
          ...item,
          parts: item.parts.filter(
            itemPart => itemPart.id !== actionTypes.payload.idPart
          )
        }))
        .filter(item => item.parts.length > 0);
      const updatedTotalRemove = calculateTotalPrice(updatedData);
      const updatedShippingCostRemove = calculateTotalShippingCost(updatedData);
      const updatedTotalPartsRemove = calculateTotalParts(updatedData);
      return {
        ...state,
        cartItems: {
          ...state.cartItems,
          isLoading: false,
          isLoadingUpdate: false,
          idPartIsLoading: null,
          errorMessages: actionTypes.payload.errorMessages,
          isError: actionTypes.payload.isError,
          data: updatedData
        },
        summary: {
          total: updatedTotalRemove,
          shippingCost: updatedShippingCostRemove,
          totalParts: updatedTotalPartsRemove
        }
      };
    case CartTypes.LOAD_CART:
    case CartTypes.CLEAR_ALL_CART:
      return {
        ...state,
        cartItems: {
          ...state.cartItems,
          isLoading: true,
          isLoadingUpdate: true,
          idPartIsLoading: null,
          errorMessages: null,
          isError: false
        }
      };
    case CartTypes.ADDING_CART:
    case CartTypes.UPDATING_CART:
      return {
        ...state,
        cartItems: {
          ...state.cartItems,
          isLoading: false,
          isLoadingUpdate: true,
          idPartIsLoading: actionTypes.payload.guidQuoteSupplierItem,
          errorMessages: null,
          isError: false
        }
      };
    case CartTypes.REMOVING_CART:
      return {
        ...state,
        cartItems: {
          ...state.cartItems,
          isLoading: false,
          isLoadingUpdate: true,
          idPartIsLoading: actionTypes.payload.idPart,
          errorMessages: null,
          isError: false
        }
      };
    case CartTypes.ERROR_CART:
      return {
        ...state,
        cartItems: {
          ...state.cartItems,
          isLoading: false,
          isLoadingUpdate: false,
          idPartIsLoading: null,
          errorMessages: null,
          isError: true
        }
      };
    case CartTypes.RESET_REDUCER_CART:
      return {
        ...state,
        cartItems: INITIAL_STATE.cartItems,
        summary: INITIAL_STATE.summary
      };
    default:
      return state;
  }
};

export default cartReducer;
